import * as React from 'react';
import type {ReactNode} from 'react';
import {more} from '@sail/icons/react/Icon';
import {message, useIntl} from '@sail/intl';
import {createViewConfig} from '@sail/engine';
import type {View} from '../view';
import {Button, ButtonConfig} from './Button';
import {Icon} from './Icon';
import {Menu, MenuGroup, MenuItem} from './Menu';
import {useOverflowCollapse} from './hooks/useOverflowCollapse';
import {css} from '../css';
import {rovingTabIndex, composite} from './rovingTabIndex';
import {isComponentType} from '../lib/isComponentType';

type TextDirection = 'ltr' | 'rtl';

export type ButtonGroupProps = View.IntrinsicElement<
  'div',
  {
    /**
     * Controls which axis the button group should span.
     * @external
     */
    direction?: 'row' | 'column';
    /**
     * Controls direction of text and horizontal overflow. Use `rtl` for languages written from right to left and `ltr` for those written from left to right.
     * @external
     */
    dir?: 'ltr' | 'rtl';

    /**
     * Controls whether or not buttons within the group collapse when there isn't enough space to display them without overflowing.
     * @external
     */
    collapse?: 'auto' | 'none';

    /**
     * Allows overriding the trigger element used for an overflow menu. Must be a component that supports press events.
     * @external
     */
    menuTrigger?: ReactNode;

    /**
     * Maximum number of buttons to display before collapsing the rest into the overflow menu.
     * When set, buttons will always collapse to this limit regardless of available space.
     * Primary buttons will still be prioritized when determining which buttons to show.
     * @external
     */
    maxVisibleButtons?: number;

    /**
     * The Buttons contained within the ButtonGroup.
     * Only `<Button>` components should be direct children. Non-Button children break
     * overflow-collapse, roving-tab-index navigation, and trigger a dev-mode console error.
     */
    children: ReactNode;
  }
>;

const overflowMenuLabel = message({
  id: '@sail/ui.buttonGroup.overflowMenuLabel',
  description: 'The aria-label for the overflow menu trigger in a button group',
  defaultMessage: 'More actions',
});

const DefaultMenuTrigger = () => {
  const {formatMessage} = useIntl();

  return (
    <Button aria-label={formatMessage(overflowMenuLabel)}>
      <Icon icon={more} />
    </Button>
  );
};

const DEFAULT_MENU_TRIGGER = <DefaultMenuTrigger />;

export const ButtonGroupConfig = createViewConfig({
  props: {} as ButtonGroupProps,
  name: 'ButtonGroup',
  defaults: {
    direction: 'row',
    collapse: 'auto',
    dir: 'ltr',
  },
  flattens: true,
});

/**
 * @external
 */
export const ButtonGroup = ButtonGroupConfig.createView(
  ({
    direction,
    collapse,
    dir,
    menuTrigger = DEFAULT_MENU_TRIGGER,
    maxVisibleButtons,
    children,
    ...props
  }) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const hasValidatedChildrenRef = React.useRef(false);

    const {hiddenChildrenIndexes} = useOverflowCollapse({
      ref,
      menuTrigger,
      collapse,
      direction,
      textDirection: dir as TextDirection,
      maxVisibleButtons,
    });

    /**
     * Use non-null children for render
     */
    const renderedChildren = React.Children.toArray(children).filter(
      (child) => !!child,
    );

    if (
      process.env.NODE_ENV !== 'production' &&
      !hasValidatedChildrenRef.current
    ) {
      hasValidatedChildrenRef.current = true;
      renderedChildren.forEach((child) => {
        if (!isComponentType(child, Button)) {
          // eslint-disable-next-line no-console
          console.error(
            `[ButtonGroup] Only <Button> components should be direct children of <ButtonGroup>. ` +
              `Non-Button children may cause accessibility issues and will not be rendered ` +
              `when the group is in a collapsed state. ` +
              `Received: ${
                React.isValidElement(child)
                  ? String(
                      typeof child.type === 'string'
                        ? child.type
                        : (child.type as {displayName?: string; name?: string})
                            .displayName ??
                            (
                              child.type as {
                                displayName?: string;
                                name?: string;
                              }
                            ).name ??
                            'Unknown',
                    )
                  : typeof child
              }. ` +
              `To fix this, ensure the child renders a <Button> directly. ` +
              `If the component wraps a <Button> but cannot be a direct <Button> itself, ` +
              `use markAsSailComponent(YourComponent, Button) to declare the relationship.`,
          );
        }
      });
    }

    return (
      <div
        {...props}
        ref={ref}
        role="toolbar"
        aria-orientation={direction === 'row' ? 'horizontal' : 'vertical'}
      >
        {React.Children.map(renderedChildren, (child) =>
          isComponentType(child, Button)
            ? React.cloneElement(child, {
                'data-priority':
                  child.props['data-priority'] ||
                  (child.props.type === 'primary' ? 1 : 2),
              })
            : child,
        )}

        {collapse === 'auto' ? (
          <Menu trigger={menuTrigger}>
            {React.Children.map(renderedChildren, (child, index) => {
              if (
                isComponentType(child, Button) &&
                hiddenChildrenIndexes.has(index)
              ) {
                const menuItem = (
                  <MenuItem
                    // @ts-expect-error - TS7005 - 'onPress' requires a PressEvent, but we don't get it from onAction
                    onAction={() => child.props.onPress?.()}
                    href={child.props.href}
                    target={child.props.target}
                    disabled={child.props.disabled}
                    type={
                      child.props.type === 'destructive'
                        ? 'destructive'
                        : 'default'
                    }
                  >
                    {child.props.children}
                  </MenuItem>
                );

                return child.props.type === 'primary' ? (
                  <MenuGroup>{menuItem}</MenuGroup>
                ) : (
                  menuItem
                );
              } else {
                return null;
              }
            })}
          </Menu>
        ) : null}
      </div>
    );
  },
  {
    css: {
      gap: 'small',
    },
    forward: {direction: true},
    variants: {
      direction: {
        row: css({stack: 'x'}),
        column: css({stack: 'y'}),
      },
    },
    uses: [composite, ButtonConfig.customize({uses: [rovingTabIndex]})],
  },
);
